import { FunctionSquare } from "lucide-react";
import { cn } from "@/lib/utils";

type FormulaModeToggleProps = {
    /** The field's form path, so the control is addressable beside its input. */
    id: string;
    /** Whether the field currently holds an expression rather than a value. */
    expression: boolean;
    onToggle: () => void;
};

/**
 * The one control that turns a value into an expression, and back.
 *
 * It is a two-state control and it says which state it is in, because that is the failure
 * mode worth spending pixels on: Companion's own documentation names the confusion between
 * a text field and an expression field as the first thing that trips its users. So the
 * button is lit and labelled « Expression » while the field holds one, faint and wordless
 * while the field holds a value, and `aria-pressed` says the same thing to a screen reader.
 *
 * It carries no opinion about what converting means — that is `toFormula` / `toLiteral`,
 * which quote and unquote so that the click never costs the author what they had typed.
 */
export function FormulaModeToggle({ id, expression, onToggle }: FormulaModeToggleProps) {
    return (
        <button
            type="button"
            aria-pressed={expression}
            aria-label={expression ? "Turn this expression back into a plain value" : "Turn this value into an expression"}
            title={
                expression
                    ? "This field is an expression, computed for each card. Click to go back to a plain value: the expression is kept, unquoted."
                    : "Turn this field into an expression, computed for each card. What is written stays, quoted."
            }
            data-testid={`formula-mode-${id}`}
            onClick={onToggle}
            className={cn(
                "ml-auto flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-none",
                expression ? "border-sky-300 bg-sky-50 font-medium text-sky-800" : "border-transparent text-gray-400 hover:border-input hover:bg-gray-50 hover:text-gray-700"
            )}
        >
            <FunctionSquare className="h-3.5 w-3.5" />
            {expression ? "Expression" : null}
        </button>
    );
}
